import React from 'react';
import clsx from 'clsx';
import { IProps, IRadioButtonOption } from './RadioButtons.interface';
import Text from '@/elements/Text/Text.component'; // Corrected import for Text component
import './RadioButtons.styles.css';

/**
 * RadioButtons component allows users to select a single option from a set.
 * It renders a group of styled buttons that behave like radio inputs.
 */
export const RadioButtons: React.FC<IProps> = ({
  name,
  options,
  selectedValue,
  onChange,
  orientation = 'horizontal',
  className,
  required = false,
  inputType = 'button', // Default to 'button' for consistency with original HTML
}) => {
  const rootClassName = clsx(
    'radioButtons',
    `radioButtons--${orientation}`,
    'radioButtons__inputType--' + inputType,
    className
  );

  // Determine if any option is currently selected to correctly set initial tabIndex
  const isAnyOptionSelected = selectedValue !== undefined && options.some(opt => opt.value === selectedValue);

  if (inputType === 'button') {

    return (
      <div
        role="radiogroup"
        aria-label={name} // Use aria-label for the group name as per common practice
        aria-required={required}
        className={rootClassName}
        // The group itself can be focusable, especially if it manages arrow key navigation.
        // For simplicity here, we make the group focusable and then individual items.
        tabIndex={0} // As per the original HTML, the group had tabindex="0"
        style={{ outline: 'none' }} // From original HTML, consider if needed or handle via CSS
      >
        {options.map((option: IRadioButtonOption, index: number) => {
          const isSelected = option.value === selectedValue;

          // WCAG: Only one radio button in a group should be in the tab order (tabindex="0") at any time.
          // If a radio button is selected, it should be the one in the tab order.
          // If no radio button is selected, the first radio button in the group should be in the tab order.
          // All other radio buttons should have tabindex="-1".
          let itemTabIndex = -1;
          if (isSelected) {
            itemTabIndex = 0;
          } else if (!isAnyOptionSelected && index === 0) {
            itemTabIndex = 0;
          }

          return (
            <button
              key={option.value}
              type="button" // Using button type as per original HTML, not input type="radio"
              role="radio"
              aria-checked={isSelected}
              data-state={isSelected ? 'checked' : 'unchecked'} // From original HTML
              value={option.value}
              className={clsx(
                'radioButtons__item',
                isSelected && 'radioButtons__item--selected'
              )}
              onClick={() => onChange(option.value)} // Changed to pass option.value
              tabIndex={itemTabIndex}
            >
              <div className="radioButtons__itemContent">
                <Text as="span" className="radioButtons__itemLabel">
                  {option.label}
                </Text>
                {option.description && (
                  <Text as="span" className="radioButtons__itemDescription">
                    {option.description}
                  </Text>
                )}
              </div>
            </button>
          );
        })}
      </div>
    );
  } else {
    return (
      <div
        role="radiogroup"
        aria-label={name}
        aria-required={required}
        className={rootClassName}
      >
        {options.map((option: IRadioButtonOption) => {
          const isSelected = option.value === selectedValue;

          return (
            <label
              key={option.value}
              className={clsx(
                'radioButtons__item',
                isSelected && 'radioButtons__item--selected'
              )}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={isSelected}
                onChange={() => onChange(option.value)} // Changed to pass option.value
                className="radioButtons__input"
              />
              <div className="radioButtons__itemContent">
                <Text as="span" className="radioButtons__itemLabel">
                  {option.label}
                </Text>
                {option.description && (
                  <Text as="span" className="radioButtons__itemDescription">
                    {option.description}
                  </Text>
                )}
              </div>
            </label>
          );
        })}
      </div>
    );
  }
};
